import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { useDeviceData } from "react-device-detect";
import { QRCode } from "react-qrcode-logo";
import Modal from "simple-react-modal";
import useSWR from "swr";
import { useAccount } from "wagmi";

import { deepCamelCaseKeys } from "../utils/camelCase";
import getDeviceModel from "../utils/deviceModel";
import { drawPath } from "../utils/drawPath";
import { Button } from "./Button";
import Logo from "./Logo";
import MintRow from "./MintRow";
import PlayerPath from "./PlayerPath";

const fetcher = (args) =>
  fetch(args)
    .then((res) => res.json())
    .then((res) => deepCamelCaseKeys(res));

const ShareModal = ({
  attempts,
  completed,
  elapsed,
  pathPoints,
  track,
  reset,
  seed,
  show,
  gameState,
  adSrc,
  adHref,
  theme,
}: {
  attempts: number;
  completed: number;
  elapsed: number;
  gameState: string;
  pathPoints: [number, number, number][];
  reset: () => void;
  track: string;
  seed: string;
  show: boolean;
  adSrc?: string;
  adHref?: string;
  theme: any;
}) => {
  const { address } = useAccount();
  const [canClose, setCanClose] = useState(false);
  const { device } = useDeviceData(window?.navigator?.userAgent);
  const screenWidth = window.screen.width;
  const screenHeight = window.screen.height;

  const deviceModel = getDeviceModel(device, screenWidth, screenHeight);

  const playerPathShare = useRef<SVGPathElement>(null);
  useEffect(() => {
    if (gameState === "crashed" || gameState === "won") {
      const _d = drawPath(pathPoints);
      playerPathShare?.current?.setAttribute(
        "d",
        _d + (gameState === "won" ? " Z" : "")
      );
    }
  }, [gameState]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setCanClose(true);
    }, 300);
    return () => {
      clearTimeout(timeout);
    };
  }, []);
  console.log({ elapsed });
  const { data } = useSWR(
    gameState === "won"
      ? `/api/personal-best?track=${seed}&address=${address}&time=${
          elapsed * 1000
        }`
      : null,
    fetcher
  );

  console.log(data);
  return (
    <Modal
      transitionSpeed={100}
      style={{
        zIndex: 11,
        transition: "all 0.2s linear",
        background: "rgba(0, 0, 0, 0.6)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        userSelect: "none",
        overflow: "auto",
      }}
      containerStyle={{
        margin: "auto",
        maxHeight: "100%",
        width: "calc(100dvh * 0.616)",
        maxWidth: "100vw",
        background: "transparent",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "space-evenly",
        padding: 0,
      }}
      closeOnOuterClick={canClose}
      show={show}
      onClose={reset}
    >
      <div
        className="relative flex flex-row w-full align-center justify-center select-none font-Bebas"
        style={{
          width: "calc(100dvh * 0.616)",
          maxWidth: "100vw",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: 0,
        }}
      >
        <div
          className="w-full h-full text-center flex flex-row mb-8"
          style={{
            background:
              gameState === "crashed" ? theme.crashBarrier : theme.background,
          }}
        >
          <h1 className="global-shadow w-full text-6xl text-white font-Bebas m-4">
            {gameState === "crashed" ? "Crashed" : "Nice Race!"}
          </h1>
        </div>
      </div>

      <div
        className="relative flex flex-row w-full align-center justify-start"
        style={{
          width: "calc(100dvh * 0.616)",
          maxWidth: "100vw",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: 0,
        }}
      >
        <div
          className="w-full h-full text-center flex flex-row"
          style={{
            background:
              gameState === "crashed" ? theme.crashBarrier : theme.background,
          }}
        >
          <svg
            id="racePath"
            style={{
              overflow: "inherit",
              flexShrink: 0,
              paddingLeft: 8,
            }}
            xmlns="http://www.w3.org/2000/svg"
            version="1.1"
            width="40%"
            viewBox="0 0 1200 1950"
          >
            <path
              d={track}
              strokeLinecap="round"
              fill="none"
              stroke={
                gameState === "crashed" ? theme.background : theme.crashBarrier
              }
              strokeWidth={132}
              strokeLinejoin="round"
              opacity={1}
            ></path>
            <path
              d={track}
              strokeLinecap="round"
              fill="none"
              stroke={
                gameState === "crashed" ? theme.crashBarrier : theme.background
              }
              strokeWidth={92}
              strokeLinejoin="round"
              opacity={1}
            ></path>
            <PlayerPath
              crashed={gameState === "crashed"}
              points={pathPoints}
              ref={playerPathShare}
              key={seed}
              attempt={attempts}
              width={22}
            />
          </svg>
          <div className="p-4 global-shadow text-xl flex flex-col justify-center items-center w-full h-3/4 mt-2  text-white font-Bebas">
            <Logo
              width={90}
              height={40}
              className="absolute top-0 md:top-1 right-3 z-10 select-none cursor-pointer event-none"
            />
            <div className="mt-0 global-shadow w-full text-8xl md:text-16xl leading-[0.2] flex flex-row justify-center items-baseline text-center">
              {gameState === "won" ? elapsed.toFixed(2) : completed.toFixed(0)}
              <span className="text-4xl md:text-6xl">
                {gameState === "won" ? "s" : "%"}
              </span>
            </div>
            {gameState !== "won" && (
              <div className="mt-0 font-Bebas global-shadow w-full text-md leading-[0.2] flex flex-row justify-center items-baseline text-center">
                ({elapsed.toFixed(2)}
                <span className="text-sm">s</span>)
              </div>
            )}
            <div className="absolute bottom-3 right-3 flex flex-row items-end text-sm  md:text-lg">
              <div className="flex flex-col items-end justify-between  mr-2 ">
                <span>Attempt #{attempts}</span>

                <div>
                  <p>{deviceModel}</p>
                </div>

                <a
                  className="text-end global-shadow mb-0 text-white font-Bebas leading-tight m-0 w-full"
                  href={`https://speedtracer.xyz/${seed}`}
                >
                  speedtracer.xyz/{seed}
                </a>
              </div>
              <QRCode
                ecLevel="L"
                value={`https://speedtracer.xyz/${seed}`}
                size={72}
                quietZone={6}
              />
            </div>
          </div>
        </div>
      </div>

      {gameState === "won" && (
        <MintRow
          elapsed={elapsed}
          seed={seed}
          theme={theme}
          attempts={attempts}
          deviceModel={deviceModel}
          rank={data?.rank}
          personalBest={data?.isPersonalBest}
        />
      )}

      {adSrc && (
        <>
          <div
            className="relative flex flex-row w-full align-center justify-start mt-4 cursor-pointer"
            style={{
              width: "calc(100dvh * 0.616)",
              maxWidth: "100vw",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: 0,
            }}
          >
            <div
              className="w-full flex-col cursor-pointer"
              style={{
                background:
                  gameState === "crashed"
                    ? theme.crashBarrier
                    : theme.background,
              }}
            >
              <a
                href={adHref}
                target="_blank"
                rel="noreferrer"
                className="cursor-pointer"
              >
                <Image
                  alt="sponsor"
                  width={728}
                  height={90}
                  src={adSrc.replace(
                    "https://ipfs.io/",
                    "https://cloudflare-ipfs.com/"
                  )}
                  className="cursor-pointer"
                />
              </a>
            </div>
          </div>
          <p className="text-white text-sm font-Bebas mt-1">Sponsor</p>
        </>
      )}

      <div
        className="w-full  text-center flex flex-row mb-0 mt-8 p-2"
        style={{
          background:
            gameState === "crashed" ? theme.crashBarrier : theme.background,
        }}
      >
        <Button
          onClick={reset}
          theme={
            gameState === "crashed"
              ? {
                  background: theme.crashBarrier,
                  crashBarrier: theme.background,
                }
              : theme
          }
          className="w-full flex flex-row items-center justify-center"
        >
          Play Again
        </Button>
      </div>
    </Modal>
  );
};

export default ShareModal;
